import { z } from 'zod';

/**
 * Состояние экрана ассистента, которое живёт в адресе.
 *
 * **Открытый разговор — часть адреса, а не память вкладки.** Пока он был локальным состоянием,
 * экран открывался пустым после каждой перезагрузки, «назад» уводил со страницы вместо предыдущего
 * разговора, а переслать разговор коллеге было нечем. Правило требует ровно этого: состояние
 * выборки живёт в URL (`rules/lists-and-filters.mdc`, §1; там же — «дровер с деталями объекта —
 * всегда в URL»).
 *
 * **`.catch(undefined)`, а не исключение.** Значение приходит снаружи — из закладки, из чата, из
 * правленой руками строки, — и упавший `validateSearch` подменяет весь экран error-boundary'ем.
 * Мусорный параметр обязан просто не выбрать разговора: тогда открывается приглашение начать новый,
 * а не белый экран на присланной ссылке.
 *
 * Имя разговора здесь не проверяется на существование — это дело сервера: чужой и несуществующий
 * разговор снаружи неразличимы и оба отвечают 404 (`rules/permissions.mdc`).
 */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/u;

export const assistantSearchSchema = z.object({
  thread: z.uuid().optional().catch(undefined),
  /**
   * День, на который отвечают, — **до** того, как разговор заведён.
   *
   * У начатого разговора условия хранит сам разговор; у неначатого их негде хранить, кроме адреса.
   * Локальное состояние здесь означало бы, что дата, выставленная руками, исчезает при перезагрузке
   * — а исчезает она незаметно, подменяясь сегодняшним днём, и вопрос о марте получает ответ по
   * действующей редакции (`rules/legal-documents.mdc`, п. 15–16).
   *
   * Отсутствие значения — не ошибка: тогда показывается сегодняшний календарный день пользователя,
   * и показывается **до** отправки, строкой рядом с полем ввода.
   */
  asOf: z.string().regex(CALENDAR_DATE).optional().catch(undefined),
});

export type AssistantSearch = z.infer<typeof assistantSearchSchema>;
